import { useState, useEffect } from "react";
import { Camera, Image as ImageIcon, FolderOpen, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { uploadNoteImage } from "@/lib/note-image-upload";

interface MediaFile {
  id: string;
  name: string;
  fileType: string;
  category: string;
  tags: string[];
}

interface MediaUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMediaSelected: (url: string) => void;
}

/**
 * Fetches a media item's served/base64 URL via the scoped content endpoint —
 * the list endpoint (GET /api/media) intentionally strips fileUrl.
 */
async function fetchMediaFileUrl(mediaId: string): Promise<string> {
  const res = await fetch(`/api/media/${mediaId}/content`, { credentials: "include" });
  if (!res.ok) throw new Error("Could not load media file");
  const data = await res.json();
  if (!data?.fileUrl) throw new Error("Media file has no content");
  return data.fileUrl as string;
}

function MediaBinThumbnail({ media, onSelect }: { media: MediaFile; onSelect: (url: string) => void }) {
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchMediaFileUrl(media.id)
      .then((url) => { if (!cancelled) setFileUrl(url); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [media.id]);

  return (
    <Card
      className="cursor-pointer hover:border-primary transition-all overflow-hidden"
      onClick={() => { if (fileUrl) onSelect(fileUrl); }}
      data-testid={`media-item-${media.id}`}
    >
      <div className="aspect-square relative flex items-center justify-center bg-muted">
        {fileUrl ? (
          <img src={fileUrl} alt={media.name} className="w-full h-full object-cover" />
        ) : error ? (
          <ImageIcon className="h-8 w-8 text-muted-foreground" />
        ) : (
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        )}
      </div>
      <div className="p-2">
        <div className="text-sm font-medium truncate">{media.name}</div>
      </div>
    </Card>
  );
}

export default function MediaUploadDialog({ open, onOpenChange, onMediaSelected }: MediaUploadDialogProps) {
  const [showMediaBin, setShowMediaBin] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();

  const { data: mediaFiles = [] } = useQuery<MediaFile[]>({
    queryKey: ["/api/media"],
    enabled: showMediaBin,
  });

  // Reset showMediaBin when dialog closes to ensure clean state on next open
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setShowMediaBin(false);
    }
    onOpenChange(newOpen);
  };

  /**
   * Uploads the picked file to object storage and registers it in the media
   * library (which claims the upload reservation), then hands the served URL
   * to the note. Keeps large images out of the database — no base64.
   */
  const uploadPickedFile = async (file: File) => {
    setIsUploading(true);
    try {
      const fileUrl = await uploadNoteImage(file);
      onMediaSelected(fileUrl);
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: "Upload Failed",
        description: error?.message || "Could not upload image",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const pickFile = (capture: boolean) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    if (capture) input.capture = 'environment';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) void uploadPickedFile(file);
    };
    input.click();
  };

  const handleTakePicture = () => pickFile(true);
  const handleChooseFromPictures = () => pickFile(false);

  const handleMediaBinClick = () => {
    setShowMediaBin(true);
  };

  const handleSelectMedia = (mediaUrl: string) => {
    onMediaSelected(mediaUrl);
    onOpenChange(false);
    setShowMediaBin(false);
  };

  const handleBack = () => {
    setShowMediaBin(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
        {!showMediaBin ? (
          <>
            <DialogHeader>
              <DialogTitle>Add Media to Note</DialogTitle>
              <DialogDescription>
                Choose how you'd like to add an image to your note
              </DialogDescription>
            </DialogHeader>
            
            <div className="flex flex-col gap-3 py-4">
              <Button
                variant="outline"
                className="w-full h-16 text-left justify-start gap-4"
                onClick={handleTakePicture}
                disabled={isUploading}
                data-testid="button-take-picture"
              >
                <Camera className="h-6 w-6" />
                <div>
                  <div className="font-medium">Take a Picture</div>
                  <div className="text-sm text-muted-foreground">Use your camera</div>
                </div>
              </Button>

              <Button
                variant="outline"
                className="w-full h-16 text-left justify-start gap-4"
                onClick={handleMediaBinClick}
                disabled={isUploading}
                data-testid="button-media-bin"
              >
                <FolderOpen className="h-6 w-6" />
                <div>
                  <div className="font-medium">Media Bin</div>
                  <div className="text-sm text-muted-foreground">Choose from your media library</div>
                </div>
              </Button>

              <Button
                variant="outline"
                className="w-full h-16 text-left justify-start gap-4"
                onClick={handleChooseFromPictures}
                disabled={isUploading}
                data-testid="button-choose-pictures"
              >
                <ImageIcon className="h-6 w-6" />
                <div>
                  <div className="font-medium">Choose from Pictures</div>
                  <div className="text-sm text-muted-foreground">Upload from your device</div>
                </div>
              </Button>

              {isUploading && (
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground" data-testid="status-media-uploading">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Uploading…
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Select from Media Bin</DialogTitle>
              <DialogDescription>
                Choose an image from your media library
              </DialogDescription>
            </DialogHeader>

            <div className="flex gap-2 mb-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleBack}
                data-testid="button-back-to-options"
              >
                Back
              </Button>
            </div>

            <ScrollArea className="h-[400px] w-full rounded-md border p-4">
              {mediaFiles.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No media files found. Upload some media first!
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {mediaFiles.map((media) => (
                    <MediaBinThumbnail key={media.id} media={media} onSelect={handleSelectMedia} />
                  ))}
                </div>
              )}
            </ScrollArea>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
